import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 60_000,
});
export const SESSION_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

function clearSession() {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_refresh_token');
  localStorage.removeItem('admin_user');
  localStorage.removeItem('admin_login_at');
}

function sessionExpired() {
  const loginAt = Number(localStorage.getItem('admin_login_at'));
  return !loginAt || (Date.now() - loginAt) > SESSION_MAX_AGE_MS;
}

function goToLogin() {
  clearSession();
  if (window.location.pathname !== '/login') window.location.href = '/login';
}

api.interceptors.request.use((config) => {
  if (sessionExpired() && localStorage.getItem('admin_token')) {
    goToLogin();
    return Promise.reject(new axios.Cancel('Session expired'));
  }
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let isRefreshing = false;
let queue = [];

function resolveQueue(error, token) {
  queue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
  queue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    const url = originalRequest?.url || '';
    const isAuthRequest = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (err.response?.status !== 401 || isAuthRequest || originalRequest._retry) {
      if (err.response?.status === 401 && !isAuthRequest) goToLogin();
      return Promise.reject(err);
    }

    const refreshToken = localStorage.getItem('admin_refresh_token');
    if (!refreshToken || sessionExpired()) {
      goToLogin();
      return Promise.reject(err);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      });
    }

    isRefreshing = true;
    try {
      const { data } = await api.post('/auth/refresh', { refresh_token: refreshToken });
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_refresh_token', data.refresh_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      resolveQueue(null, data.token);
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      return api(originalRequest);
    } catch (refreshErr) {
      resolveQueue(refreshErr, null);
      goToLogin();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

const cache = new Map();
const CACHE_TTL = 20_000; // short TTL — admin data should still feel fresh, just not refetched on every tab flip

function cachedGet(key, fn) {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < CACHE_TTL) return Promise.resolve({ data: hit.data });
  return fn().then(res => {
    cache.set(key, { data: res.data, ts: Date.now() });
    return res;
  });
}

function invalidateCache(prefix) {
  if (!prefix) { cache.clear(); return; }
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const adminAPI = {
  getStatsOverview: () => cachedGet('stats-overview', () => api.get('/admin/stats/overview')),
  getUsageStats: (range = '7d') => cachedGet(`usage-stats-${range}`, () => api.get('/admin/stats/usage', { params: { range } })),

  getUsers: (params) => cachedGet(`users-${JSON.stringify(params || {})}`, () => api.get('/admin/users', { params })),
  createUser: (data) => api.post('/admin/users', data).then(r => { invalidateCache('users-'); invalidateCache('stats-overview'); return r; }),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data).then(r => { invalidateCache('users-'); return r; }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then(r => { invalidateCache('users-'); invalidateCache('stats-overview'); return r; }),

  getReports: (params) => cachedGet(`admin-reports-${JSON.stringify(params || {})}`, () => api.get('/admin/reports', { params })),
  getReportById: (id) => cachedGet(`admin-report-${id}`, () => api.get(`/admin/reports/${id}`)),
  getModalities: () => cachedGet('modalities', () => api.get('/modalities')),
  getBodyParts: () => cachedGet('body-parts', () => api.get('/body-parts')),
  getBodyPartsAdmin: (params) => cachedGet(`body-parts-admin-${JSON.stringify(params || {})}`, () => api.get('/admin/body_parts', { params })),
  createBodyPart: (data) => api.post('/admin/body_parts', data).then(r => { invalidateCache('body-parts'); return r; }),
  updateBodyPart: (id, data) => api.patch(`/admin/body_parts/${id}`, data).then(r => { invalidateCache('body-parts'); return r; }),

  getModalitiesAdmin: (params) => cachedGet(`modalities-admin-${JSON.stringify(params || {})}`, () => api.get('/admin/modalities', { params })),
  createModality: (data) => api.post('/admin/modalities', data).then(r => { invalidateCache('modalities'); return r; }),
  updateModality: (id, data) => api.patch(`/admin/modalities/${id}`, data).then(r => { invalidateCache('modalities'); return r; }),
  getActivityLog: (params) => cachedGet(`activity-log-${JSON.stringify(params || {})}`, () => api.get('/admin/activity-log', { params })),
  getActivityLogExport: (params) => api.get('/admin/activity-log/export', { params, responseType: 'blob' }),
  getFeedback: (params) => cachedGet(`admin-feedback-${JSON.stringify(params || {})}`, () => api.get('/admin/feedback', { params })),
  deleteFeedback: (reportId, created_at) => api.delete(`/admin/feedback/${reportId}`, { params: { created_at } }).then(r => { invalidateCache('admin-feedback-'); return r; }),
  updateFeedbackStatus: (reportId, data) => api.patch(`/admin/feedback/${reportId}/status`, data).then(r => { invalidateCache('admin-feedback-'); return r; }),
  getTechniquesAdmin: (params) => cachedGet(`techniques-admin-${JSON.stringify(params || {})}`, () => api.get('/admin/techniques', { params })),
  createTechnique: (data) => api.post('/admin/techniques', data).then(r => { invalidateCache('techniques'); return r; }),
  updateTechnique: (id, data) => api.patch(`/admin/techniques/${id}`, data).then(r => { invalidateCache('techniques'); return r; }),
};

export default api;